import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sessions } from '../api';
import { RefreshCw, Globe, Phone, Clock, DollarSign, Bot, CheckCircle, AlertCircle, Loader2, Sparkles, Tag, Target, FileText, Zap, ChevronLeft, ChevronRight, Database, Gauge } from 'lucide-react';
import Toast from './Toast';
import LoadingSpinner from './LoadingSpinner';
import { VisitorInline } from './VisitorBadge';

const SessionAnalysis = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionsList, setSessionsList] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const sessionsPerPage = 10;

  useEffect(() => {
    const urlSessionId = searchParams.get('sessionId');
    if (urlSessionId) {
      setTimeout(() => {
        loadSessionAnalysis(urlSessionId);
      }, 500);
      return;
    }
    // Fallback: legacy localStorage path
    const selectedSessionId = localStorage.getItem('selectedSessionForAnalysis');
    if (selectedSessionId) {
      localStorage.removeItem('selectedSessionForAnalysis');
      setTimeout(() => {
        loadSessionAnalysis(selectedSessionId);
      }, 1000);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [currentPage]);

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await sessions.list(currentPage, sessionsPerPage);
      const data = response.data;
      const sorted = (data.sessions || []).sort((a, b) => {
        if (a.status === 'ENDED' && b.status !== 'ENDED') return -1;
        if (b.status === 'ENDED' && a.status !== 'ENDED') return 1;
        return new Date(b.started_at) - new Date(a.started_at);
      });
      setSessionsList(sorted);
      setTotalSessions(data.total || 0);
    } catch (err) {
      setError('Failed to load sessions');
      setToast({ message: 'Failed to load sessions', type: 'error' });
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSessionAnalysis = async (sessionId) => {
    try {
      setLoading(true);
      setError('');
      const response = await sessions.getAnalysis(sessionId);
      setAnalysisData(response.data);
      setSelectedSession(sessionId);
    } catch (err) {
      setError('Failed to load session analysis: ' + (err.response?.data?.detail || err.message));
      setAnalysisData(null);
      setToast({ message: 'Failed to load session analysis', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const triggerAnalysis = async (sessionId) => {
    try {
      setAnalyzing(true);
      setError('');
      await sessions.analyze(sessionId);
      setToast({ message: 'Analysis triggered successfully', type: 'success' });
      setTimeout(() => loadSessionAnalysis(sessionId), 2000);
    } catch (err) {
      setError('Failed to trigger analysis: ' + (err.response?.data?.detail || err.message));
      setToast({ message: 'Failed to trigger analysis', type: 'error' });
    } finally {
      setAnalyzing(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': return 'text-success';
      case 'negative': return 'text-destructive';
      case 'neutral': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const formatLatencyDisplay = (ms) => {
    if (ms == null) return null;
    const rounded = Math.round(ms);
    const sec = (ms / 1000).toFixed(2);
    return `${rounded}ms (${sec}s)`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ended': return 'bg-success/20 text-success border-success/30';
      case 'active': return 'bg-primary/20 text-primary border-primary/30';
      case 'completed': return 'bg-success/20 text-success border-success/30';
      case 'voicemail_reached': return 'bg-blue-500/20 text-blue-600 border-blue-500/30 dark:text-blue-400';
      default: return 'bg-muted/20 text-muted-foreground border-border';
    }
  };

  const getChannelIcon = (channel) => {
    return channel === 'phone' ? <Phone className="w-4 h-4" /> : <Globe className="w-4 h-4" />;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-foreground">Session Analysis</h2>
          <p className="text-sm text-muted-foreground">Analyze session details and conversation insights</p>
        </div>
        <button
          onClick={loadSessions}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 min-h-0 overflow-hidden">
        {/* Left Column - Sessions List */}
        <div className="rounded-lg border border-border/60 bg-card/80 backdrop-blur-sm p-3 flex flex-col min-h-0 shadow-elegant overflow-hidden animate-slide-in-left">
          <div className="flex items-center gap-2 mb-2 flex-shrink-0">
            <FileText className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Sessions</h3>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2 pr-2 min-h-0"
            style={{ 
              scrollBehavior: 'smooth',
              maxHeight: 'calc(100vh - 220px)'
            }}
          >
            {loadingSessions ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : (
              <>
            {sessionsList.map((session) => (
              <div
                key={session.id}
                onClick={() => {
                  loadSessionAnalysis(session.id);
                  setSearchParams(prev => {
                    const next = new URLSearchParams(prev);
                    next.set('sessionId', session.id);
                    return next;
                  }, { replace: true });
                }}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedSession === session.id
                        ? 'bg-primary/10 border-primary/50 shadow-lg shadow-primary/10'
                        : 'bg-card/50 border-border/50 hover:bg-muted/30 hover:border-border'
                }`}
              >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-muted-foreground flex-shrink-0">
                          {getChannelIcon(session.channel)}
                        </span>
                        <span className="text-sm font-medium text-foreground truncate">
                          {session.agent_name || 'Unknown'}
                  </span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${getStatusColor(session.status)}`}>
                    {session.status}
                  </span>
                </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span className="truncate">{formatDuration(session.duration)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <DollarSign className="w-3 h-3" />
                        <span className="truncate">${session.cost_usd?.toFixed(4) || '0.0000'}</span>
                      </div>
                      <div className="text-muted-foreground text-right truncate">
                        {new Date(session.started_at).toLocaleDateString()}
                      </div>
                </div>
                <VisitorInline visitor={session.visitor} />
              </div>
            ))}
                {sessionsList.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">No sessions found</p>
                  </div>
                )}
              </>
            )}
          </div>
          {/* Pagination */}
          {totalSessions > sessionsPerPage && (
            <div className="border-t border-border/50 pt-2 mt-2 flex-shrink-0">
              <div className="flex items-center justify-between text-xs">
                <div className="text-muted-foreground">
                  Showing {((currentPage - 1) * sessionsPerPage) + 1} to {Math.min(currentPage * sessionsPerPage, totalSessions)} of {totalSessions}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1 || loadingSessions}
                    className="p-1.5 rounded border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-2 text-sm text-foreground">
                    {currentPage} / {Math.ceil(totalSessions / sessionsPerPage)}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalSessions / sessionsPerPage), p + 1))}
                    disabled={currentPage >= Math.ceil(totalSessions / sessionsPerPage) || loadingSessions}
                    className="p-1.5 rounded border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
          </div>
            </div>
          )}
        </div>

        {/* Right Column - Session Details */}
        <div className="rounded-lg border border-border/60 bg-card/80 backdrop-blur-sm p-3 flex flex-col min-h-0 shadow-elegant overflow-hidden animate-slide-in-right animate-delay-100">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <LoadingSpinner size="lg" text="Loading analysis..." />
            </div>
          ) : analysisData ? (
            <div className="flex-1 overflow-y-auto space-y-3 pr-2 min-h-0"
              style={{ 
                scrollBehavior: 'smooth',
                maxHeight: 'calc(100vh - 220px)'
              }}
            >
              {/* Session Overview */}
              <div className="bg-muted/20 rounded-lg border border-border/50 p-3">
                <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Bot className="w-4 h-4 text-primary" />
                  Session Overview
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Agent</p>
                    <p className="text-sm font-medium text-foreground">{analysisData.agent?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Channel</p>
                    <div className="flex items-center gap-1.5 text-sm text-foreground">
                      {getChannelIcon(analysisData.session?.channel)}
                      <span className="capitalize">{analysisData.session?.channel || 'N/A'}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Duration</p>
                    <p className="text-sm font-medium text-foreground">{formatDuration(analysisData.session?.duration_seconds)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Cost</p>
                    <p className="text-sm font-medium text-success">
                      {analysisData.session?.cost_usd ? `$${analysisData.session.cost_usd.toFixed(4)}` : 'N/A'}
                    </p>
                  </div>
                  {analysisData.session?.avg_response_time_ms != null && !analysisData.analysis && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                        <Gauge className="w-3 h-3" />
                        End to end latency
                      </p>
                      <p
                        className={`text-sm font-medium ${
                          Math.round(analysisData.session.avg_response_time_ms) <= 1400 ? 'text-success' : 'text-warning'
                        }`}
                      >
                        {formatLatencyDisplay(analysisData.session.avg_response_time_ms)}
                      </p>
                    </div>
                  )}
                  {analysisData.session?.caller_number && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">From</p>
                      <p className="text-sm font-medium text-foreground">{analysisData.session.caller_number}</p>
                    </div>
                  )}
                  {analysisData.session?.called_number && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">To</p>
                      <p className="text-sm font-medium text-foreground">{analysisData.session.called_number}</p>
                    </div>
                  )}
                </div>

                {/* Recording Player */}
                {(() => {
                  const sessionFromList = sessionsList.find(s => s.id === selectedSession);
                  const recordingUrl = sessionFromList?.recording_url ? sessions.recordingUrl(sessionFromList.id) : null;
                  return recordingUrl ? (
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <p className="text-xs text-muted-foreground mb-2">Recording</p>
                      <div className="flex justify-center">
                        <audio controls className="w-full h-12">
                          <source src={recordingUrl} type="audio/ogg" />
                          <source src={recordingUrl} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Analysis Results */}
              {analysisData.analysis ? (
                (() => {
                  const a = analysisData.analysis;
                  // Retention cleanup nulls transcript/topics/entities/action_items/qa_metrics/extracted_data
                  // once a session ages past the org's retention window. When that's happened, only
                  // "Processed At" survives -- show the minimal view instead of an analysis full of blanks.
                  const hasContent = a.summary || a.transcript ||
                    (a.topics && a.topics.length > 0) ||
                    (a.entities && a.entities.length > 0) ||
                    (a.action_items && a.action_items.length > 0) ||
                    (a.qa_metrics && Object.keys(a.qa_metrics).length > 0) ||
                    (a.extracted_data && Object.keys(a.extracted_data).length > 0);

                  if (!hasContent) {
                    return (
                      <div className="bg-muted/20 rounded-lg border border-border/50 p-4">
                        <h3 className="text-base font-semibold text-foreground flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-primary" />
                          Analysis Results
                        </h3>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Processed At</p>
                          <p className="text-sm text-foreground">{formatDate(a.processed_at)}</p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      <div className="bg-muted/20 rounded-lg border border-border/50 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-primary" />
                            Analysis Results
                          </h3>
                          {a.sentiment && (
                            <span className={`text-xs px-2 py-1 rounded-full border ${getSentimentColor(a.sentiment)}`}>
                              {a.sentiment}
                            </span>
                          )}
                        </div>

                        <div className="mb-3">
                          <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                            <FileText className="w-3 h-3" />
                            Summary
                          </p>
                          <p className="text-sm text-foreground leading-relaxed">{a.summary || 'No summary available'}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Status</p>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${
                              a.analysis_status === 'completed' ? 'bg-success/20 text-success border-success/30' :
                              a.analysis_status === 'processing' ? 'bg-warning/20 text-warning border-warning/30' :
                              a.analysis_status === 'failed' ? 'bg-destructive/20 text-destructive border-destructive/30' :
                              'bg-muted/20 text-muted-foreground border-border'
                            }`}>
                              {a.analysis_status === 'completed' && <CheckCircle className="w-3 h-3" />}
                              {a.analysis_status === 'failed' && <AlertCircle className="w-3 h-3" />}
                              {a.analysis_status}
                            </span>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Processed At</p>
                            <p className="text-sm text-foreground">{formatDate(a.processed_at)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Topics and Entities */}
                      <div className="grid grid-cols-1 gap-3">
                        {a.topics && a.topics.length > 0 && (
                          <div className="bg-muted/20 rounded-lg border border-border/50 p-3">
                            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                              <Tag className="w-4 h-4 text-primary" />
                              Topics
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                              {a.topics.map((topic, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-primary/10 border border-primary/30 text-primary rounded-full text-xs"
                                >
                                  {topic}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {a.entities && a.entities.length > 0 && (
                          <div className="bg-muted/20 rounded-lg border border-border/50 p-3">
                            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                              <Target className="w-4 h-4 text-primary" />
                              Entities
                            </h4>
                            <div className="space-y-1.5">
                              {a.entities.map((entity, index) => (
                                <div key={index} className="flex items-center justify-between text-xs py-1">
                                  <span className="text-muted-foreground">{entity.type}</span>
                                  <span className="text-foreground font-medium">{entity.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Items */}
                      {a.action_items && a.action_items.length > 0 && (
                        <div className="bg-muted/20 rounded-lg border border-border/50 p-4">
                          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                            <CheckCircle className="w-4 h-4 text-primary" />
                            Action Items
                          </h4>
                          <div className="space-y-2">
                            {a.action_items.map((item, index) => (
                              <div key={index} className="p-2 bg-background/50 rounded border border-border/50">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-foreground">{item.title}</span>
                                  {item.due && <span className="text-xs text-muted-foreground">{item.due}</span>}
                                </div>
                                {item.owner && <p className="text-xs text-muted-foreground">Owner: {item.owner}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* QA Metrics */}
                      {((a.qa_metrics && Object.keys(a.qa_metrics).length > 0) ||
                        analysisData.session?.avg_response_time_ms != null) && (
                        <div className="bg-muted/20 rounded-lg border border-border/50 p-4">
                          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                            <Sparkles className="w-4 h-4 text-primary" />
                            Quality Metrics
                          </h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {analysisData.session?.avg_response_time_ms != null && (
                              <div className="p-2 bg-background/50 rounded border border-border/50">
                                <p className="text-muted-foreground mb-0.5 flex items-center gap-1">
                                  <Gauge className="w-3 h-3" />
                                  End to end latency
                                </p>
                                <p
                                  className={`font-medium ${
                                    Math.round(analysisData.session.avg_response_time_ms) <= 1400 ? 'text-success' : 'text-warning'
                                  }`}
                                >
                                  {formatLatencyDisplay(analysisData.session.avg_response_time_ms)}
                                </p>
                              </div>
                            )}
                            {a.qa_metrics &&
                              Object.entries(a.qa_metrics).map(([key, value]) => (
                                <div key={key} className="p-2 bg-background/50 rounded border border-border/50">
                                  <p className="text-muted-foreground mb-0.5">{key.replace(/_/g, ' ')}</p>
                                  <p className="text-foreground font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</p>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Extracted Data */}
                      {a.extracted_data && Object.keys(a.extracted_data).length > 0 && (
                        <div className="bg-muted/20 rounded-lg border border-border/50 p-4">
                          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                            <Database className="w-4 h-4 text-primary" />
                            Extracted Data
                          </h4>
                          <div className="grid grid-cols-1 gap-2 text-xs">
                            {Object.entries(a.extracted_data).map(([key, value]) => (
                              <div key={key} className="p-2 bg-background/50 rounded border border-border/50">
                                <p className="text-muted-foreground mb-1 font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                                <p className="text-foreground">
                                  {value === null || value === undefined ? (
                                    <span className="text-muted-foreground italic">Not found</span>
                                  ) : typeof value === 'boolean' ? (
                                    <span className={value ? 'text-success' : 'text-muted-foreground'}>
                                      {value ? 'Yes' : 'No'}
                                    </span>
                                  ) : typeof value === 'object' ? (
                                    <span className="font-mono text-xs">{JSON.stringify(value, null, 2)}</span>
                                  ) : (
                                    <span className="font-medium">{String(value)}</span>
                                  )}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Transcript */}
                      {a.transcript && (
                        <div className="bg-muted/20 rounded-lg border border-border/50 p-4">
                          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                            <FileText className="w-4 h-4 text-primary" />
                            Transcript
                          </h4>
                          <div className="bg-background/50 p-3 rounded border border-border/50 max-h-48 overflow-y-auto">
                            <pre className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed">
                              {a.transcript}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                    <p className="text-sm text-muted-foreground mb-4">No analysis available for this session</p>
                    {analysisData.session?.status === 'ENDED' && (
                    <button
                      onClick={() => triggerAnalysis(selectedSession)}
                      disabled={analyzing}
                        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                    >
                      {analyzing ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                          Analyzing...
                          </>
                      ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            Trigger Analysis
                          </>
                      )}
                    </button>
                  )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-sm text-muted-foreground mb-1">Select a session to view analysis</p>
                <p className="text-xs text-muted-foreground">Choose from the list on the left</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default SessionAnalysis;
